import { Search, MapPin, Calendar, Clock } from 'lucide-react';
import { useState } from 'react';
import type { PackageSearchFilters } from '../types/marketplaceSearch';
import { EMPTY_PACKAGE_SEARCH_FILTERS } from '../types/marketplaceSearch';

const MONTHS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
] as const;

const DURATION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Durée du séjour' },
  { value: '1-3', label: '1 à 3 jours' },
  { value: '4-7', label: '4 à 7 jours' },
  { value: '8-14', label: '8 à 14 jours' },
  { value: '15+', label: '15 jours et +' },
];

type SearchBarProps = {
  variant?: 'default' | 'hero';
  onSearch?: (filters: PackageSearchFilters) => void;
};

export default function SearchBar({ variant = 'default', onSearch }: SearchBarProps) {
  const [destination, setDestination] = useState('');
  const [month, setMonth] = useState('');
  const [duration, setDuration] = useState('');

  const isHero = variant === 'hero';
  /** Hauteur commune pour alignement visuel des champs et du bouton */
  const controlH = 'h-12';
  const inputBase = isHero
    ? `w-full ${controlH} pl-11 pr-3 rounded-xl border border-white/20 bg-white/95 text-[15px] text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/80 shadow-sm`
    : `w-full ${controlH} pl-12 pr-4 rounded-lg border border-gray-300 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`;
  const selectBase = isHero
    ? `w-full ${controlH} pl-11 pr-9 rounded-xl border border-white/20 bg-white/95 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/80 appearance-none shadow-sm`
    : `w-full ${controlH} pl-12 pr-4 rounded-lg border border-gray-300 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none`;
  const iconClass = isHero ? 'text-gray-500' : 'text-gray-400';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const filters: PackageSearchFilters = {
      destination: destination.trim(),
      category: '',
      month,
      duration,
      packageType: '',
      airlineId: '',
    };
    onSearch?.(filters);
  };

  const handleReset = () => {
    setDestination('');
    setMonth('');
    setDuration('');
    onSearch?.(EMPTY_PACKAGE_SEARCH_FILTERS);
  };

  return (
    <form
      onSubmit={handleSearch}
      className={
        isHero
          ? 'w-full max-w-5xl mx-auto'
          : 'w-full max-w-4xl'
      }
    >
      <div
        className={
          isHero
            ? 'rounded-2xl bg-white/10 backdrop-blur-md border border-white/25 p-4 md:p-5 shadow-xl'
            : ''
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-12 md:items-stretch gap-3 md:gap-3">
          <div className="relative flex md:col-span-4 min-w-0">
            <MapPin className={`absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 w-5 h-5 ${iconClass} pointer-events-none`} />
            <input
              type="text"
              placeholder="Destination"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className={inputBase}
              aria-label="Destination"
            />
          </div>

          <div className="relative flex min-w-0 md:col-span-3">
            <Calendar className={`absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 w-5 h-5 ${iconClass} pointer-events-none`} />
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className={selectBase}
              aria-label="Mois de départ"
            >
              <option value="">Mois de départ</option>
              {MONTHS.map((m, index) => (
                <option key={m} value={String(index + 1)}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="relative flex min-w-0 md:col-span-3">
            <Clock className={`absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 w-5 h-5 ${iconClass} pointer-events-none`} />
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className={selectBase}
              aria-label="Durée du séjour"
            >
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.label + opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex md:col-span-2">
            <button
              type="submit"
              className={`w-full inline-flex ${controlH} items-center justify-center gap-2 rounded-xl bg-blue-600 text-[15px] font-medium text-white px-4 hover:bg-blue-700 transition shadow-md`}
            >
              <Search className="w-5 h-5 shrink-0" />
              Rechercher
            </button>
          </div>
        </div>

        {isHero && (
          <button
            type="button"
            onClick={handleReset}
            className="mt-3 text-sm text-white/85 hover:text-white underline-offset-2 hover:underline"
          >
            Réinitialiser les critères
          </button>
        )}
      </div>
    </form>
  );
}
